// Dashboard panels (plan step 3.5, D98), the parts that are plain data: the board's time ranges,
// the API's limits mirrored for the editor, the presets and their parameters, and how a run's
// answer becomes something to draw. A panel's title, chart and SQL may have been written by an
// agent: everything here reads them defensively and never assumes a column exists.
import { ApiError } from '../api/client'
import type {
  DashboardPanel,
  PanelInput,
  PanelPatch,
  QueryResult,
  SeriesMetric,
} from '../api/types'
import type { MessageId, Translator } from '../i18n'

export const RANGES = ['1h', '24h', '7d', '30d'] as const
export type RangeKey = (typeof RANGES)[number]

const HOUR = 3_600_000
const RANGE_MS: Record<RangeKey, number> = {
  '1h': HOUR,
  '24h': 24 * HOUR,
  '7d': 7 * 24 * HOUR,
  '30d': 30 * 24 * HOUR,
}

/** The window a board's range covers, ending now (the API widens it to whole buckets). */
export function rangeOf(key: RangeKey, now = Date.now()): { from: string; to: string } {
  return { from: new Date(now - RANGE_MS[key]).toISOString(), to: new Date(now).toISOString() }
}

/** The API's limits (apps/api read/dashboards.ts, packages/accounts dashboards.ts). */
export const LIMITS = {
  dashboards: 20,
  panels: 24,
  title: 100,
  sql: 10_000,
  y: 6,
  column: 128,
  unit: 16,
} as const

/** How often a board may refresh itself: a run costs the plan's query rate. */
export const AUTO_REFRESH_MS = 5 * 60_000

export const CHART_TYPES = ['line', 'bar', 'stat', 'table'] as const
export type ChartType = (typeof CHART_TYPES)[number]

export interface PanelChart {
  type: ChartType
  x?: string | undefined
  y?: string[] | undefined
  series?: string | undefined
  unit?: string | undefined
  threshold?: number | undefined
}

export interface PanelLayout {
  x: number
  y: number
  w: number
  h: number
}

const text = (v: unknown, max: number): string | undefined =>
  typeof v === 'string' && v.length > 0 && v.length <= max ? v : undefined

/** A panel's chart as stored: anything not of its shape is dropped. */
export function chartOf(raw: Record<string, unknown>): PanelChart {
  const type = (CHART_TYPES as readonly unknown[]).includes(raw.type)
    ? (raw.type as ChartType)
    : 'table'
  const y = Array.isArray(raw.y)
    ? raw.y
        .map((v) => text(v, LIMITS.column))
        .filter((v): v is string => v !== undefined)
        .slice(0, LIMITS.y)
    : undefined
  const threshold =
    typeof raw.threshold === 'number' && Number.isFinite(raw.threshold) ? raw.threshold : undefined
  return {
    type,
    x: text(raw.x, LIMITS.column),
    y: y?.length ? y : undefined,
    series: text(raw.series, LIMITS.column),
    unit: text(raw.unit, LIMITS.unit),
    threshold,
  }
}

const int = (v: unknown, min: number, max: number, fallback: number): number =>
  typeof v === 'number' && Number.isInteger(v) ? Math.max(min, Math.min(max, v)) : fallback

/** A panel's place on the 12-column grid, kept inside it. */
export function layoutOf(raw: Record<string, number>): PanelLayout {
  const w = int(raw.w, 1, 12, 6)
  return {
    x: Math.min(int(raw.x, 0, 11, 0), 12 - w),
    y: int(raw.y, 0, 1000, 0),
    w,
    h: int(raw.h, 1, 12, 4),
  }
}

/** Where a new panel goes: under the others, at the left. */
export function nextLayout(
  panels: { layout: Record<string, number> }[],
  w = 6,
  h = 4,
): PanelLayout {
  const bottom = panels.reduce((max, p) => {
    const l = layoutOf(p.layout)
    return Math.max(max, l.y + l.h)
  }, 0)
  return { x: 0, y: Math.min(bottom, 1000), w, h }
}

/** The board's reading order: by row, then column (and the phone's single column). */
export function byPlace<T extends { layout: Record<string, number> }>(panels: T[]): T[] {
  return [...panels].sort((a, b) => {
    const la = layoutOf(a.layout)
    const lb = layoutOf(b.layout)
    return la.y - lb.y || la.x - lb.x
  })
}

// Presets (apps/api read/dashboard-presets.ts): every plan, unmetered.

export const PRESETS = [
  'crash_free',
  'performance_series',
  'network_endpoints',
  'vitals',
  'release_health',
] as const
export type PresetName = (typeof PRESETS)[number]

export const SERIES_METRICS: readonly SeriesMetric[] = [
  'cold_start_p90',
  'ttid_p90',
  'network_p90',
  'network_error_rate',
  'hang_rate',
  'anr_rate',
  'slow_frames_per_minute',
  'frozen_frames_per_minute',
]

export interface ParamField {
  key: string
  kind: 'app' | 'text' | 'int' | 'enum'
  label: MessageId
  required?: boolean
  max?: number
  min?: number
  values?: readonly { value: string; label: MessageId }[]
}

const appParam = (required = false): ParamField => ({
  key: 'app_id',
  kind: 'app',
  label: 'dash.param.app',
  required,
})
const releaseParam: ParamField = {
  key: 'release',
  kind: 'text',
  label: 'dash.param.release',
  max: 128,
}

/** Each preset's parameters, with the API's rules. */
export const PRESET_PARAMS: Record<PresetName, ParamField[]> = {
  crash_free: [appParam(), releaseParam],
  performance_series: [
    appParam(true),
    {
      key: 'metric',
      kind: 'enum',
      label: 'dash.param.metric',
      required: true,
      values: SERIES_METRICS.map((m) => ({ value: m, label: `perf.trend.${m}` as MessageId })),
    },
    {
      key: 'by',
      kind: 'enum',
      label: 'dash.param.by',
      values: [
        { value: 'day', label: 'dash.param.by.day' },
        { value: 'release', label: 'dash.param.by.release' },
      ],
    },
  ],
  network_endpoints: [
    appParam(),
    releaseParam,
    { key: 'host', kind: 'text', label: 'dash.param.host', max: 255 },
    {
      key: 'sort',
      kind: 'enum',
      label: 'dash.param.sort',
      values: [
        { value: 'requests', label: 'dash.param.sort.requests' },
        { value: 'error_rate', label: 'dash.param.sort.error_rate' },
        { value: 'p90', label: 'dash.param.sort.p90' },
      ],
    },
    { key: 'limit', kind: 'int', label: 'dash.param.limit', min: 1, max: 200 },
  ],
  vitals: [appParam(), releaseParam],
  release_health: [
    appParam(true),
    { key: 'a', kind: 'text', label: 'dash.param.a', required: true, max: 128 },
    { key: 'b', kind: 'text', label: 'dash.param.b', required: true, max: 128 },
  ],
}

/** The chart a preset starts with. */
export const PRESET_CHART: Record<PresetName, ChartType> = {
  crash_free: 'line',
  performance_series: 'line',
  network_endpoints: 'table',
  vitals: 'stat',
  release_health: 'stat',
}

export const isPreset = (v: unknown): v is PresetName => (PRESETS as readonly unknown[]).includes(v)

// The editor's draft: every field as typed, checked against the API's rules before it is sent.

export interface PanelDraft {
  kind: 'sql' | 'preset'
  title: string
  sql: string
  preset: PresetName
  params: Record<string, string>
  type: ChartType
  x: string
  y: string
  series: string
  unit: string
  threshold: string
  w: string
  h: string
}

export function draftOf(panel?: DashboardPanel, sqlDefault = ''): PanelDraft {
  const chart = chartOf(panel?.chart ?? {})
  const layout = layoutOf(panel?.layout ?? {})
  const preset = isPreset(panel?.preset) ? panel.preset : 'crash_free'
  return {
    kind: panel?.kind ?? 'sql',
    title: panel?.title ?? '',
    sql: panel?.sql ?? sqlDefault,
    preset,
    params: Object.fromEntries(Object.entries(panel?.params ?? {}).map(([k, v]) => [k, String(v)])),
    type: panel ? chart.type : 'line',
    x: chart.x ?? '',
    y: (chart.y ?? []).join(', '),
    series: chart.series ?? '',
    unit: chart.unit ?? '',
    threshold: chart.threshold === undefined ? '' : String(chart.threshold),
    w: String(layout.w),
    h: String(layout.h),
  }
}

export interface Problem {
  field: string
  message: MessageId
  values?: Record<string, number>
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
// biome-ignore lint/suspicious/noControlCharactersInRegex: the API refuses control characters
const UNSAFE = /[\u0000-\u001f\u007f-\u009f‪-‮⁦-⁩]/

const yList = (y: string): string[] =>
  y
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)

const whole = (v: string): number | null => (/^\d+$/.test(v.trim()) ? Number(v.trim()) : null)

/** What is wrong with a draft, by field, before anything is sent. */
export function checkDraft(d: PanelDraft): Problem[] {
  const out: Problem[] = []
  const title = d.title.trim()
  if (!title) out.push({ field: 'title', message: 'dash.v.required' })
  else if (title.length > LIMITS.title)
    out.push({ field: 'title', message: 'dash.v.long', values: { max: LIMITS.title } })
  else if (UNSAFE.test(title)) out.push({ field: 'title', message: 'dash.v.text' })
  if (d.kind === 'sql') {
    if (!d.sql.trim()) out.push({ field: 'sql', message: 'dash.v.required' })
    else if (d.sql.length > LIMITS.sql)
      out.push({ field: 'sql', message: 'dash.v.long', values: { max: LIMITS.sql } })
  } else {
    for (const p of PRESET_PARAMS[d.preset]) {
      const v = (d.params[p.key] ?? '').trim()
      if (!v) {
        if (p.required) out.push({ field: `param.${p.key}`, message: 'dash.v.required' })
        continue
      }
      if (p.kind === 'app' && !UUID.test(v))
        out.push({ field: `param.${p.key}`, message: 'dash.v.required' })
      if (p.kind === 'text' && p.max !== undefined && v.length > p.max)
        out.push({ field: `param.${p.key}`, message: 'dash.v.long', values: { max: p.max } })
      if (p.kind === 'int') {
        const n = whole(v)
        const min = p.min ?? 0
        const max = p.max ?? 0
        if (n === null || n < min || n > max)
          out.push({ field: `param.${p.key}`, message: 'dash.v.int', values: { min, max } })
      }
    }
  }
  if (d.kind === 'sql') {
    const y = yList(d.y)
    if (y.length > LIMITS.y)
      out.push({ field: 'y', message: 'dash.v.y', values: { max: LIMITS.y } })
    for (const [field, value] of [
      ['x', d.x.trim()],
      ['series', d.series.trim()],
      ...y.map((v) => ['y', v]),
    ] as const)
      if (value.length > LIMITS.column || UNSAFE.test(value))
        out.push({ field, message: 'dash.v.long', values: { max: LIMITS.column } })
  }
  if (d.unit.trim().length > LIMITS.unit || UNSAFE.test(d.unit))
    out.push({ field: 'unit', message: 'dash.v.long', values: { max: LIMITS.unit } })
  if (d.threshold.trim() && !Number.isFinite(Number(d.threshold)))
    out.push({ field: 'threshold', message: 'dash.v.number' })
  for (const field of ['w', 'h'] as const) {
    const n = whole(d[field])
    if (n === null || n < 1 || n > 12)
      out.push({ field, message: 'dash.v.int', values: { min: 1, max: 12 } })
  }
  return out
}

/** A chart as the API takes it: no empty fields. */
type ChartInput = PanelInput['chart']

/** The chart a draft describes, without empty fields. */
function draftChart(d: PanelDraft): ChartInput {
  const y = yList(d.y)
  const chart: ChartInput = { type: d.type }
  if (d.kind === 'sql') {
    if (d.x.trim()) chart.x = d.x.trim()
    if (y.length) chart.y = y
    if (d.series.trim()) chart.series = d.series.trim()
  }
  if (d.unit.trim()) chart.unit = d.unit.trim()
  if (d.threshold.trim()) chart.threshold = Number(d.threshold)
  return chart
}

function draftParams(d: PanelDraft): Record<string, number | string> {
  const out: Record<string, number | string> = {}
  for (const p of PRESET_PARAMS[d.preset]) {
    const v = (d.params[p.key] ?? '').trim()
    if (v) out[p.key] = p.kind === 'int' ? Number(v) : v
  }
  return out
}

/** A checked draft as the API takes a new panel, at `at` on the grid. */
export function draftInput(d: PanelDraft, at: { x: number; y: number }): PanelInput {
  const w = Number(d.w)
  const layout = { x: Math.min(at.x, 12 - w), y: at.y, w, h: Number(d.h) }
  const chart = draftChart(d)
  return d.kind === 'sql'
    ? { kind: 'sql', title: d.title.trim(), sql: d.sql, chart, layout }
    : {
        kind: 'preset',
        title: d.title.trim(),
        preset: d.preset,
        params: draftParams(d),
        chart,
        layout,
      }
}

/**
 * The change a checked draft makes to a panel. The SQL goes only when it changed (or the kind
 * did): sending it asks the plan for SQL panels, which a title change should not.
 */
export function draftPatch(d: PanelDraft, panel: DashboardPanel): PanelPatch {
  const input = draftInput(d, layoutOf(panel.layout))
  const patch: PanelPatch = {
    title: input.title,
    chart: input.chart,
    layout: input.layout,
  }
  if (input.kind !== panel.kind) patch.kind = input.kind
  if (input.kind === 'sql' && (input.sql !== panel.sql || input.kind !== panel.kind))
    patch.sql = input.sql
  if (input.kind === 'preset') {
    patch.preset = input.preset
    patch.params = input.params ?? {}
  }
  return patch
}

// A run's answer, as something to draw.

export type ValueFormat = 'number' | 'percent' | 'ms' | 'perMinute'

export interface FrameColumn {
  name: string
  type: string
  format?: ValueFormat | undefined
}

/** Columns and rows, from SQL or shaped from a preset's answer. */
export interface Frame {
  columns: FrameColumn[]
  rows: unknown[][]
  rowCount: number
  truncated: boolean
}

export interface Stat {
  label: MessageId
  value: number | null
  format: ValueFormat
}

export type PanelView =
  | { kind: 'frame'; frame: Frame; chart: PanelChart }
  | { kind: 'stats'; caption: string | null; stats: Stat[] }
  | { kind: 'unknown' }

export function frameOfResult(r: QueryResult): Frame {
  return {
    columns: r.columns.map((c) => ({ name: c.name, type: c.type })),
    rows: r.rows,
    rowCount: r.row_count,
    truncated: r.truncated,
  }
}

const NUMERIC = /^(Nullable\(|LowCardinality\()*(U?Int\d+|Float\d+|Decimal)/
export const isNumericType = (type: string): boolean => NUMERIC.test(type)
export const isTimeType = (type: string): boolean =>
  /^(Nullable\(|LowCardinality\()*(Date|DateTime)/.test(type)

/** A cell as a number: 64-bit integers arrive as strings. */
export function toNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }
  return null
}

const records = (v: unknown, key: string): Record<string, unknown>[] => {
  const list = (v as Record<string, unknown> | null)?.[key]
  return Array.isArray(list)
    ? list.filter((x): x is Record<string, unknown> => typeof x === 'object' && x !== null)
    : []
}
const field = (o: unknown, ...path: string[]): unknown =>
  path.reduce<unknown>(
    (at, k) => (at && typeof at === 'object' ? (at as Record<string, unknown>)[k] : undefined),
    o,
  )
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

function frameOf(columns: FrameColumn[], list: Record<string, unknown>[]): Frame {
  return {
    columns,
    rows: list.map((r) => columns.map((c) => r[c.name] ?? null)),
    rowCount: list.length,
    truncated: false,
  }
}

const seriesFormat = (metric: unknown): ValueFormat =>
  typeof metric !== 'string'
    ? 'number'
    : metric.endsWith('_p90')
      ? 'ms'
      : metric.endsWith('_rate')
        ? 'percent'
        : 'perMinute'

/**
 * A preset's answer (the route it reads, as it answers) as a view: days and points as lines,
 * endpoints as a table, vitals and a release comparison as a few figures.
 */
export function presetView(preset: string, data: unknown, chart: PanelChart): PanelView {
  // The preset decides the columns; the panel picks how to draw them.
  const withDefaults = (x: string, y: string[]): PanelChart => ({ ...chart, x, y })
  switch (preset) {
    case 'crash_free':
      return {
        kind: 'frame',
        chart: withDefaults('day', ['crash_free_sessions_rate']),
        frame: frameOf(
          [
            { name: 'day', type: 'Date' },
            { name: 'crash_free_sessions_rate', type: 'Float64', format: 'percent' },
            { name: 'crash_free_users_rate', type: 'Float64', format: 'percent' },
            { name: 'sessions', type: 'UInt64' },
            { name: 'crashed_sessions', type: 'UInt64' },
          ],
          records(data, 'days'),
        ),
      }
    case 'performance_series': {
      const format = seriesFormat(field(data, 'metric'))
      return {
        kind: 'frame',
        chart: withDefaults('key', ['value']),
        frame: frameOf(
          [
            { name: 'key', type: field(data, 'by') === 'release' ? 'String' : 'Date' },
            { name: 'value', type: 'Float64', format },
            { name: 'count', type: 'UInt64' },
          ],
          records(data, 'points'),
        ),
      }
    }
    case 'network_endpoints':
      return {
        kind: 'frame',
        chart: withDefaults('template', ['requests']),
        frame: frameOf(
          [
            { name: 'method', type: 'String' },
            { name: 'host', type: 'String' },
            { name: 'template', type: 'String' },
            { name: 'requests', type: 'UInt64' },
            { name: 'error_rate', type: 'Float64', format: 'percent' },
            { name: 'p90_ms', type: 'Float64', format: 'ms' },
            // spans, or approximate: from the rollups when no span was released (D114, APS-445)
            { name: 'percentiles', type: 'String' },
          ],
          records(data, 'endpoints'),
        ),
      }
    case 'vitals': {
      const releases = records(data, 'releases')
      if (chart.type === 'table')
        return {
          kind: 'frame',
          chart,
          frame: frameOf(
            [
              { name: 'release', type: 'String' },
              { name: 'cold_start_p90_ms', type: 'Float64', format: 'ms' },
              { name: 'hang_rate', type: 'Float64', format: 'percent' },
              { name: 'anr_rate', type: 'Float64', format: 'percent' },
              { name: 'sessions', type: 'UInt64' },
            ],
            releases.map((r) => ({
              release: r.release,
              cold_start_p90_ms: num(field(r, 'app_start', 'cold', 'p90_ms')),
              hang_rate: num(field(r, 'hangs', 'hang_rate')),
              anr_rate: num(field(r, 'hangs', 'anr_rate')),
              sessions: num(field(r, 'hangs', 'sessions')),
            })),
          ),
        }
      const first = releases[0]
      return {
        kind: 'stats',
        caption: typeof first?.release === 'string' ? first.release : null,
        stats: [
          {
            label: 'dash.stat.coldStart',
            value: num(field(first, 'app_start', 'cold', 'p90_ms')),
            format: 'ms',
          },
          {
            label: 'dash.stat.hangRate',
            value: num(field(first, 'hangs', 'hang_rate')),
            format: 'percent',
          },
          {
            label: 'dash.stat.anrRate',
            value: num(field(first, 'hangs', 'anr_rate')),
            format: 'percent',
          },
          {
            label: 'dash.stat.sessions',
            value: num(field(first, 'hangs', 'sessions')),
            format: 'number',
          },
        ],
      }
    }
    case 'release_health': {
      const rate = (side: 'a' | 'b') => num(field(data, side, 'crash_free_sessions_rate'))
      const b = field(data, 'b', 'release')
      if (data === null || typeof data !== 'object') return { kind: 'unknown' }
      return {
        kind: 'stats',
        caption: typeof b === 'string' ? b : null,
        stats: [
          { label: 'dash.stat.crashFreeB', value: rate('b'), format: 'percent' },
          { label: 'dash.stat.crashFreeA', value: rate('a'), format: 'percent' },
          {
            label: 'dash.stat.adoption',
            value: num(field(data, 'b', 'adoption', 'sessions_share')),
            format: 'percent',
          },
          {
            label: 'dash.stat.newIssues',
            value: records(data, 'new_issues').length,
            format: 'number',
          },
        ],
      }
    }
    default:
      return { kind: 'unknown' }
  }
}

export interface Line {
  name: string
  values: (number | null)[]
}

export interface Plot {
  /** The x values, as the result has them (formatted when drawn). */
  labels: unknown[]
  xType: string
  lines: Line[]
  format: ValueFormat
}

export type PlotResult = { ok: true; plot: Plot } | { ok: false; missing: string | null }

/**
 * The lines a frame draws: x is the chart's column (else the first), y its columns (else every
 * numeric one, at most 6), and a series column splits the first y into one line per value.
 */
export function plotOf(frame: Frame, chart: PanelChart): PlotResult {
  const index = (name: string) => frame.columns.findIndex((c) => c.name === name)
  const xi = chart.x ? index(chart.x) : 0
  if (xi < 0) return { ok: false, missing: chart.x ?? null }
  const si = chart.series ? index(chart.series) : -1
  if (chart.series && si < 0) return { ok: false, missing: chart.series }
  const yNames =
    chart.y ??
    frame.columns
      .filter((c, i) => i !== xi && i !== si && isNumericType(c.type))
      .map((c) => c.name)
      .slice(0, LIMITS.y)
  const missing = yNames.find((name) => index(name) < 0)
  if (missing) return { ok: false, missing }
  const first = yNames[0]
  if (first === undefined) return { ok: false, missing: null }
  const xColumn = frame.columns[xi]
  const format = frame.columns[index(first)]?.format ?? 'number'
  if (si < 0)
    return {
      ok: true,
      plot: {
        labels: frame.rows.map((r) => r[xi]),
        xType: xColumn?.type ?? 'String',
        lines: yNames.map((name) => {
          const yi = index(name)
          return { name, values: frame.rows.map((r) => toNumber(r[yi])) }
        }),
        format,
      },
    }
  // One line per series value (the first 6 met), over every x value met, in order.
  const yi = index(first)
  const keyOf = (v: unknown) =>
    typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)
  const xs = new Map<string, number>()
  const labels: unknown[] = []
  const lines = new Map<string, Map<number, number | null>>()
  for (const row of frame.rows) {
    const xk = keyOf(row[xi])
    if (!xs.has(xk)) {
      xs.set(xk, labels.length)
      labels.push(row[xi])
    }
    const sk = keyOf(row[si])
    if (!lines.has(sk)) {
      if (lines.size >= LIMITS.y) continue
      lines.set(sk, new Map())
    }
    lines.get(sk)?.set(xs.get(xk) ?? 0, toNumber(row[yi]))
  }
  return {
    ok: true,
    plot: {
      labels,
      xType: xColumn?.type ?? 'String',
      lines: [...lines].map(([name, values]) => ({
        name,
        values: labels.map((_, i) => values.get(i) ?? null),
      })),
      format,
    },
  }
}

/** A chart to start from, for rows just run in Explore. */
export function guessChart(columns: { name: string; type: string }[], type: ChartType): ChartInput {
  if (type === 'table') return { type }
  const numeric = columns.filter((c) => isNumericType(c.type)).map((c) => c.name)
  if (type === 'stat') return numeric[0] ? { type, y: [numeric[0]] } : { type }
  const x = columns[0]?.name
  const y = numeric.filter((n) => n !== x).slice(0, LIMITS.y)
  return { type, ...(x ? { x } : {}), ...(y.length ? { y } : {}) }
}

/** A dashboard error, in the reader's words (never the server's message). */
export function dashboardError(t: Translator['t'], error: unknown): string {
  if (!(error instanceof ApiError)) return t('state.failed')
  switch (error.code) {
    case 'plan_required':
      return t('dash.error.plan')
    case 'grant_required':
      return t('dash.error.grant')
    case 'forbidden':
      return t('dash.error.forbidden')
    case 'plan_limit':
      return t('dash.error.limit', { boards: LIMITS.dashboards, panels: LIMITS.panels })
    case 'rate_limited':
      return t('dash.error.rate')
    case 'not_found':
      return t('dash.error.notFound')
    case 'invalid':
    case 'invalid_request':
      return t('dash.error.invalid')
    default:
      return error.status === 503 ? t('dash.error.unavailable') : t('state.failed')
  }
}

/**
 * At most `size` panel runs at once, the others in turn: a board of 24 panels never spends the
 * plan's query rate, or the project's two concurrent queries, all at the same moment.
 */
export class RunQueue {
  private active = 0
  private readonly waiting: (() => void)[] = []

  constructor(readonly size = 2) {}

  async run<T>(fn: () => Promise<T>, signal?: AbortSignal): Promise<T> {
    while (this.active >= this.size) await new Promise<void>((go) => this.waiting.push(go))
    if (signal?.aborted) {
      // Its turn came after it was no longer wanted: the next one takes it.
      this.waiting.shift()?.()
      throw signal.reason ?? new DOMException('Aborted', 'AbortError')
    }
    this.active++
    try {
      return await fn()
    } finally {
      this.active--
      this.waiting.shift()?.()
    }
  }
}
